/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { IResponseData } from "@/utilities/types/requests.ts";
import useApi from "@/hooks/useApi.tsx";
import { useTableServer } from "@/hooks/useTableServer.tsx";
import { useCallback, useEffect, useState } from "react";
import ControlledTable from "@/components/controlled-table";
import { useColumn } from "@/hooks/use-column.ts";
import { Roles } from "@/utilities/types/Users.ts";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import { Button, Popover, Text, Title } from "rizzui";
import { backPrev } from "@/utilities/functions";
import { routes } from "@/config/routes";
import { useUnitsColumns } from "./columns";
import UnitsRepository from "@/utilities/repositories/Units";
import { IUnit, IUnitDTO } from "@/utilities/types/Units";
import cn from "@/utils/class-names";
import useProfile from "@/hooks/use-profile";
import FilterElement from "./filter-element";
import { BulkImportModal } from "../bulk-import";

const UnitsTable = () => {
  const { projectId, floorPlanId } = useParams<{
    projectId: string;
    floorPlanId: string;
  }>();

  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const { profile } = useProfile();

  const { request: createUnit } = useApi<IResponseData<IUnit>>({
    request: UnitsRepository.createUnit,
    enableToast: true,
  });

  const { request: updateUnit } = useApi<IResponseData<IUnit>>({
    request: UnitsRepository.updateUnit,
    enableToast: true,
  });

  const { request: deleteUnitItem } = useApi<IResponseData<IUnit>>({
    request: UnitsRepository.removeUnit,
    enableToast: true,
  });

  const {
    handleReset,
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handleSort,
    sortConfig,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    response,
    filters,
    isFiltered,
    updateFilter,
    refresh,
  } = useTableServer({
    server: {
      request: UnitsRepository.getUnits,
    },
    initialFilterState: {
      projectId,
      floorPlanId,
    },
    initialSortConfig: {
      key: "price",
      direction: "asc",
    },
  });

  const [customTableData, setCustomTableData] =
    useState<(IUnit & { isAdd?: boolean })[]>(tableData);

  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState<boolean>(false);
  const [isMarkAllAsSoldLoading, setIsMarkAllAsSoldLoading] = useState(false);

  const onAddUnit = useCallback(
    async (data: IUnitDTO) => {
      await createUnit({
        ...data,
        floorPlanId,
        projectId,
      })
        .then(() => {
          setIsAdding(false);
          refresh(); // Recall API
        })
        .catch((err: any) => {
          toast.error(err?.message || "Something went wrong");
        });

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [createUnit, floorPlanId, refresh, projectId],
  );

  const onUpdateItem = useCallback(
    async (id: string, data: Partial<IUnitDTO>) => {
      await updateUnit(id, data);
      refresh(); // Recall API
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [refresh, updateUnit],
  );

  const onDeleteItem = useCallback(
    async (id: string) => {
      if (isAdding) {
        setCustomTableData(tableData);
        setIsAdding(false);
        return;
      }
      await deleteUnitItem(id);
      refresh(); // Recall API
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [deleteUnitItem, refresh, isAdding, tableData],
  );

  useEffect(() => {
    setCustomTableData(tableData);
  }, [tableData]);

  const columns = useUnitsColumns({
    data: customTableData,
    sortConfig,
    onDeleteItem,
    onHeaderCellClick,
    projectId: projectId ?? "",
    reset: handleReset,
    role: Roles.ADMIN,
    onUpdateItem,
    isAdding,
    onAddUnit,
  });

  const addNewRow = () => {
    setIsAdding(true);
    setCustomTableData([
      {
        ...customTableData[0],
        isAdd: true,
        id: "addNew",
        name: "",
        status: "available",
        price: 0,
      },

      ...customTableData,
    ]);
  };

  const handleBulkImport = useCallback(
    async (units: Partial<IUnit>[]) => {
      const unitsWithIds: Partial<IUnit>[] = units.map((unit) => ({
        name: unit.name,
        price: unit.price,
        status: unit.status,
        floorPlanId,
        projectId,
      }));

      try {
        await UnitsRepository.bulkCreateUnits(unitsWithIds);
        await refresh(); // Recall API - must await to ensure tableData updates before modal closes
        toast.success(`Successfully inserted ${units.length} units`);
      } catch (err: any) {
        // Handle duplicate name errors more gracefully
        if (err?.message?.includes("already exists")) {
          toast.error(
            `Insert failed: Some units have duplicate names. Please check your CSV file and remove duplicates.`,
          );
        } else {
          toast.error(err?.message || "Bulk insert failed");
        }
      }
    },
    [floorPlanId, projectId, refresh],
  );

  const handleMarkAllAsSold = useCallback(async () => {
    if (!projectId || !floorPlanId) {
      return;
    }

    setIsMarkAllAsSoldLoading(true);
    try {
      const result = await UnitsRepository.markAllAsSold(projectId, floorPlanId);
      const updated = (result as any)?.data?.updated ?? 0;
      await refresh();
      toast.success(`${updated} unit(s) marked as sold`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to mark all units as sold");
    } finally {
      setIsMarkAllAsSoldLoading(false);
    }
  }, [floorPlanId, projectId, refresh]);

  const { visibleColumns } = useColumn(columns);
  return (
    <div className={"col-span-full mt-4"}>
      <div className="mb-4 w-full flex items-center justify-between">
        <Title as="h5">Units</Title>
        <div className="flex gap-2 items-center justify-center">
          <Link to={backPrev(routes.project.listing)} className="">
            <span
              className={
                "px-4 py-2 inline-block border border-solid border-black rounded-md"
              }
            >
              Back
            </span>
          </Link>
          {profile?.role === Roles.ADMIN && (
            <div className="flex gap-2">
              <Button
                disabled={isAdding}
                onClick={() => setIsBulkImportOpen(true)}
              >
                Bulk Insert
              </Button>
              <Popover placement="bottom-end">
                <Popover.Trigger>
                  <Button
                    color="danger"
                    disabled={isAdding || isMarkAllAsSoldLoading}
                    isLoading={isMarkAllAsSoldLoading}
                  >
                    Mark all as Sold
                  </Button>
                </Popover.Trigger>
                <Popover.Content className="z-10">
                  {({ setOpen }) => (
                    <div className="w-56 pb-2 pt-1 text-left">
                      <Title as="h6" className="mb-0.5 text-sm text-gray-700">
                        Mark all as sold?
                      </Title>
                      <Text className="mb-2 leading-relaxed text-gray-500">
                        All units in this floor plan will be marked as sold.
                      </Text>
                      <div className="flex items-center justify-end">
                        <Button
                          size="sm"
                          className="me-1.5 h-7"
                          onClick={async () => {
                            try {
                              await handleMarkAllAsSold();
                            } finally {
                              setOpen(false);
                            }
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => setOpen(false)}
                        >
                          No
                        </Button>
                      </div>
                    </div>
                  )}
                </Popover.Content>
              </Popover>
              <Button disabled={isAdding} onClick={addNewRow}>
                Add new
              </Button>
            </div>
          )}
        </div>
      </div>
      <div
        className={
          "col-span-full mt-4 flex flex-col lg:flex-row justify-between gap-2 lg:gap-4"
        }
      >
        <div className="mb-4 w-full  flex gap-x-2 items-center">
          <div className="flex flex-row items-center gap-x-2">
            <Title
              as="h6"
              className="text-xs font-semibold uppercase  xs:text-sm"
            >
              TOTAL:
            </Title>
            <Text className="leading-[1.7]">
              {response?.data?.totalUnitCount ?? totalItems} units
            </Text>
          </div>
          <div className="flex flex-row items-center gap-x-2">
            <Title
              as="h6"
              className="text-xs font-semibold uppercase  xs:text-sm"
            >
              Available:
            </Title>
            <Text className="leading-[1.7]">
              {response?.data?.availableUnitCount ?? 0} units
            </Text>
          </div>
          <div className="flex flex-row items-center gap-x-2">
            <Title as="h6" className="text-xs font-semibold uppercase xs:text-sm">Sold:</Title>
            <Text className="leading-[1.7]">{response?.data?.soldUnitCount ?? 0} units</Text>
          </div>
          <div className="flex flex-row items-center gap-x-2">
            <Title as="h6" className="text-xs font-semibold uppercase xs:text-sm">Reserved:</Title>
            <Text className="leading-[1.7]">{response?.data?.reservedUnitCount ?? 0} units</Text>
          </div>
          <div className="flex flex-row items-center gap-x-2">
            <Title as="h6" className="text-xs font-semibold uppercase xs:text-sm">Not released:</Title>
            <Text className="leading-[1.7]">{response?.data?.notReleasedUnitCount ?? 0} units</Text>
          </div>
        </div>

        <div className="mb-4 min-w-max flex gap-x-2 items-center">
          <FilterElement
            filters={filters}
            isFiltered={isFiltered}
            handleReset={handleReset}
            updateFilter={updateFilter}
          />
        </div>
      </div>

      <ControlledTable
        variant="modern"
        data={customTableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-ignore
        columns={visibleColumns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        rowClassName={cn(
          isAdding &&
            "data-[row-key=addNew]:opacity-100 data-[row-key=addNew]:pointer-events-auto opacity-20 pointer-events-none",
        )}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />

      <BulkImportModal
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onImport={handleBulkImport}
        existingUnits={tableData
          .filter(
            (unit: any): unit is IUnit & { name: string } => unit.name !== null,
          )
          .map((unit: any) => ({ name: unit.name }))}
      />

    </div>
  );
};

export default UnitsTable;
